import { z } from "zod";
import { timezoneOffsetMinutes, timezoneOverlap } from "./matching";
import type { GOALS, LEVELS } from "./schemas";

/**
 * Descoberta de devs (seção 7.4): constelações de stack, busca em linguagem natural, horário em
 * comum, motivos do match e o montador de time. Tudo puro: o servidor e as telas usam o mesmo
 * cálculo, e os testes cobrem sem banco.
 */

// ───────────── Constelações (grupos de stack) ─────────────

export const STACK_GROUPS = [
  {
    id: "front",
    label: "Front-end",
    skills: [
      "javascript",
      "typescript",
      "react",
      "vue",
      "svelte",
      "angular",
      "nextjs",
      "css",
      "tailwind",
      "html",
    ],
  },
  {
    id: "back",
    label: "Back-end",
    skills: [
      "nodejs",
      "nestjs",
      "deno",
      "python",
      "django",
      "fastapi",
      "go",
      "rust",
      "java",
      "spring",
      "kotlin",
      "csharp",
      "dotnet",
      "php",
      "laravel",
      "ruby",
      "elixir",
      "graphql",
      "microservices",
      "kafka",
      "system-design",
    ],
  },
  {
    id: "dados",
    label: "Dados e IA",
    skills: ["sql", "postgres", "mysql", "mongodb", "redis", "ml", "data", "ai", "algorithms"],
  },
  { id: "mobile", label: "Mobile", skills: ["mobile", "flutter", "react-native", "swift"] },
  {
    id: "infra",
    label: "Infra e DevOps",
    skills: [
      "docker",
      "kubernetes",
      "aws",
      "gcp",
      "azure",
      "terraform",
      "linux",
      "devops",
      "cicd",
      "git",
      "testing",
    ],
  },
  { id: "games", label: "Games e gráficos", skills: ["webgl", "threejs", "cpp", "c"] },
  { id: "seguranca", label: "Segurança", skills: ["security", "appsec", "pentest", "devsecops"] },
  { id: "design", label: "Design", skills: ["design"] },
] as const;

export type StackGroupId = (typeof STACK_GROUPS)[number]["id"];

const GROUP_OF = new Map<string, StackGroupId[]>();
for (const g of STACK_GROUPS) for (const s of g.skills) GROUP_OF.set(s, [...(GROUP_OF.get(s) ?? []), g.id]);

export const stackGroupLabel = (id: StackGroupId) => STACK_GROUPS.find((g) => g.id === id)?.label ?? id;

/** Constelação principal de quem tem essas habilidades (a de maior soma de proficiência). */
export function stackGroupOf(
  skills: ReadonlyArray<{ slug: string; proficiency: number }>,
): StackGroupId | null {
  const score = new Map<StackGroupId, number>();
  for (const s of skills)
    for (const g of GROUP_OF.get(s.slug) ?? [])
      score.set(g, (score.get(g) ?? 0) + Math.max(1, s.proficiency));
  let best: StackGroupId | null = null;
  let max = 0;
  for (const g of STACK_GROUPS) {
    const v = score.get(g.id) ?? 0;
    if (v > max) {
      max = v;
      best = g.id;
    }
  }
  return best;
}

// ───────────── Horário em comum ─────────────

/** Horas locais em que, sem histórico, consideramos a pessoa por perto. */
const DEFAULT_LOCAL_HOURS = { from: 9, to: 23 } as const;

/**
 * Horas UTC (24 posições) em que a pessoa costuma estar programando. Com histórico suficiente
 * (sessões das últimas semanas, por hora UTC), valem as horas com atividade; sem ele, das 9h às
 * 23h no fuso do perfil.
 */
export function usualHoursUtc(
  activity: readonly number[] | null | undefined,
  timezone: string | null,
  at = new Date(),
): boolean[] {
  const counts = activity && activity.length === 24 ? activity : null;
  const total = counts ? counts.reduce((a, b) => a + b, 0) : 0;
  if (counts && total >= 3) {
    const peak = Math.max(...counts);
    const on = counts.map((c) => c > 0 && c >= peak * 0.2);
    // Uma hora isolada entre duas ativas também conta (intervalo do almoço, café).
    return on.map((v, h) => v || (on[(h + 23) % 24]! && on[(h + 1) % 24]!));
  }
  const offset = (timezone ? timezoneOffsetMinutes(timezone, at) : null) ?? -180;
  const hours = Array<boolean>(24).fill(false);
  for (let local = DEFAULT_LOCAL_HOURS.from; local < DEFAULT_LOCAL_HOURS.to; local++) {
    const utc = (((local - offset / 60) % 24) + 24) % 24;
    hours[Math.floor(utc)] = true;
  }
  return hours;
}

/** Quantas horas por dia as duas (ou mais) pessoas costumam estar ao mesmo tempo. */
export function commonHours(...people: ReadonlyArray<readonly boolean[]>): number {
  if (!people.length) return 0;
  let n = 0;
  for (let h = 0; h < 24; h++) if (people.every((p) => p[h])) n++;
  return n;
}

/** Converte as horas UTC para o fuso de quem olha (para desenhar a faixa de 24 h). */
export function toLocalHours(utc: readonly boolean[], timezone: string | null, at = new Date()): boolean[] {
  const offset = (timezone ? timezoneOffsetMinutes(timezone, at) : null) ?? -at.getTimezoneOffset();
  const shift = Math.round(offset / 60);
  return Array.from({ length: 24 }, (_, local) => utc[(((local - shift) % 24) + 24) % 24] ?? false);
}

// ───────────── Motivos do match ─────────────

export const LANGUAGE_NAMES: Record<string, string> = {
  pt: "português",
  en: "inglês",
  es: "espanhol",
  fr: "francês",
  de: "alemão",
  it: "italiano",
  ja: "japonês",
  zh: "chinês",
};

export const languageName = (code: string) => LANGUAGE_NAMES[code.toLowerCase()] ?? code.toUpperCase();

export interface ReasonProfile {
  skills: ReadonlyArray<{ slug: string; label: string; proficiency: number }>;
  languages: readonly string[];
  hours: readonly boolean[];
}

/**
 * Por que essa pessoa apareceu, em frases curtas ("Domina WebGL, que você procurou · 4h de horário
 * em comum · ambos falam português"). No máximo `max` motivos, do mais forte ao mais fraco.
 */
export function matchReasons(
  me: ReasonProfile,
  dev: ReasonProfile,
  wanted: readonly string[] = [],
  max = 3,
): string[] {
  const out: string[] = [];
  const mine = new Map(me.skills.map((s) => [s.slug, s.proficiency]));
  const strong = dev.skills.filter((s) => s.proficiency >= 3);
  const asked = strong.filter((s) => wanted.includes(s.slug));
  if (asked.length)
    out.push(
      `Domina ${asked
        .map((s) => s.label)
        .slice(0, 2)
        .join(" e ")}, que você procurou`,
    );
  else {
    const teaches = strong.find((s) => s.proficiency >= 4 && (mine.get(s.slug) ?? 0) <= 2);
    if (teaches)
      out.push(
        mine.has(teaches.slug)
          ? `Domina ${teaches.label}, que você ainda está aprendendo`
          : `Domina ${teaches.label}, que falta no seu perfil`,
      );
  }
  const hours = commonHours(me.hours, dev.hours);
  if (hours > 0) out.push(`${hours}h de horário em comum`);
  const myLangs = new Set(me.languages.map((l) => l.toLowerCase()));
  const shared = dev.languages.find((l) => myLangs.has(l.toLowerCase()));
  if (shared) out.push(`Ambos falam ${languageName(shared)}`);
  const together = dev.skills.filter((s) => mine.has(s.slug)).length;
  if (together >= 2) out.push(`${together} habilidades em comum`);
  return out.slice(0, max);
}

// ───────────── Compatibilidade da Órbita ─────────────

export interface OrbitPerson {
  skills: ReadonlyArray<{ slug: string; label: string; proficiency: number }>;
  languages: readonly string[];
  goal: string | null;
  timezone: string | null;
  /** Horas (UTC) em que costuma programar. */
  hours?: readonly boolean[];
}

export interface OrbitCandidate extends OrbitPerson {
  /** Habilidades de um pedido de ajuda aberto. */
  helpSkills?: readonly string[];
  /** Sessões que já fizeram juntos. */
  pairs?: number;
  /** Seguem um ao outro. */
  friends?: boolean;
}

/** Pesos da distância na Órbita (somam 1). Ponto de partida, não verdade. */
export const KOMPIL_WEIGHTS = {
  stack: 0.3,
  exchange: 0.2,
  language: 0.15,
  hours: 0.15,
  timezone: 0.1,
  bond: 0.1,
} as const;

export interface OrbitCompatibility {
  /** 0–1: quanto mais alto, mais perto de você na Órbita. */
  score: number;
  percent: number;
  /** Habilidades em comum (rótulos, das mais fortes para as mais fracas). */
  shared: string[];
  /** Troca de conhecimento em poucas palavras ("quer aprender · você ensina React"). */
  exchange: string | null;
  /** Idioma em comum (código). */
  language: string | null;
  commonHours: number;
  pairs: number;
  friends: boolean;
}

/**
 * Compatibilidade de uma pessoa com quem olha a Órbita: stack em comum, troca de conhecimento (o
 * que um quer aprender e o outro ensina, ou um pedido de ajuda que você sabe responder), idioma,
 * horário ativo em comum, fuso e o histórico de vocês (quem já pareou fica mais perto).
 */
export function orbitCompatibility(
  me: OrbitPerson,
  dev: OrbitCandidate,
  at = new Date(),
): OrbitCompatibility {
  const mine = new Map(me.skills.map((s) => [s.slug, s.proficiency]));
  const theirs = new Map(dev.skills.map((s) => [s.slug, s.proficiency]));
  const shared = dev.skills
    .filter((s) => mine.has(s.slug))
    .sort((a, b) => b.proficiency + (mine.get(b.slug) ?? 0) - (a.proficiency + (mine.get(a.slug) ?? 0)))
    .map((s) => s.label);
  const stack = Math.min(1, shared.length / 3);

  // Troca: do sinal mais forte (pedido de ajuda) ao mais fraco (só a diferença de stack).
  const labelOf = (slug: string) =>
    dev.skills.find((s) => s.slug === slug)?.label ?? me.skills.find((s) => s.slug === slug)?.label ?? slug;
  const iTeach = me.skills.find((s) => s.proficiency >= 4 && (theirs.get(s.slug) ?? 0) <= 2);
  const theyTeach = dev.skills.find((s) => s.proficiency >= 4 && (mine.get(s.slug) ?? 0) <= 2);
  const helpable = (dev.helpSkills ?? []).find((slug) => (mine.get(slug) ?? 0) >= 3);
  let exchange: string | null = null;
  let exchangeScore = 0;
  if (helpable) {
    exchange = `pediu ajuda com ${labelOf(helpable)} (você sabe)`;
    exchangeScore = 1;
  } else if (dev.goal === "aprender" && me.goal === "ensinar") {
    exchange = iTeach ? `quer aprender · você ensina ${iTeach.label}` : "quer aprender · você quer ensinar";
    exchangeScore = 1;
  } else if (dev.goal === "ensinar" && me.goal === "aprender") {
    exchange = theyTeach
      ? `ensina ${theyTeach.label} · você quer aprender`
      : "quer ensinar · você quer aprender";
    exchangeScore = 1;
  } else if (theyTeach) {
    exchange = `domina ${theyTeach.label}, que falta para você`;
    exchangeScore = 0.6;
  }

  const myLangs = new Set(me.languages.map((l) => l.toLowerCase()));
  const language = dev.languages.find((l) => myLangs.has(l.toLowerCase()))?.toLowerCase() ?? null;
  const languageScore = language ? 1 : !myLangs.size || !dev.languages.length ? 0.5 : 0;

  const hours = me.hours && dev.hours ? commonHours(me.hours, dev.hours) : 0;
  const hoursScore = me.hours && dev.hours ? Math.min(1, hours / 6) : 0.5;
  const timezone = timezoneOverlap(me.timezone, dev.timezone, at);

  const pairs = Math.max(0, dev.pairs ?? 0);
  const friends = Boolean(dev.friends);
  const bond = pairs > 0 ? Math.min(1, 0.6 + 0.2 * pairs) : friends ? 0.6 : 0;

  const score =
    KOMPIL_WEIGHTS.stack * stack +
    KOMPIL_WEIGHTS.exchange * exchangeScore +
    KOMPIL_WEIGHTS.language * languageScore +
    KOMPIL_WEIGHTS.hours * hoursScore +
    KOMPIL_WEIGHTS.timezone * timezone +
    KOMPIL_WEIGHTS.bond * bond;
  const clamped = Math.max(0, Math.min(1, score));
  return {
    score: clamped,
    percent: Math.round(clamped * 100),
    shared,
    exchange,
    language,
    commonHours: hours,
    pairs,
    friends,
  };
}

/**
 * A hora (no fuso de quem olha) em que mais dos seus melhores matches costumam programar, a partir
 * de agora: "seus melhores matches costumam aparecer às 20h". Null sem dados.
 */
export function bestMatchHour(
  people: ReadonlyArray<readonly boolean[] | undefined>,
  timezone: string | null,
  at = new Date(),
): { hour: number; people: number } | null {
  const counts = Array<number>(24).fill(0);
  for (const h of people) {
    if (!h) continue;
    toLocalHours(h, timezone, at).forEach((on, i) => {
      if (on) counts[i]!++;
    });
  }
  const now = at.getHours();
  let best: { hour: number; people: number } | null = null;
  // A partir da próxima hora, dando a volta no dia: em empate, a mais próxima ganha.
  for (let k = 1; k <= 24; k++) {
    const hour = (now + k) % 24;
    if (counts[hour]! > (best?.people ?? 0)) best = { hour, people: counts[hour]! };
  }
  return best;
}

// ───────────── Busca em linguagem natural ─────────────

export interface NaturalFilters {
  skills: string[];
  lang?: string;
  available?: boolean;
  online?: boolean;
  goal?: (typeof GOALS)[number];
  level?: (typeof LEVELS)[number];
  country?: string;
  /** Texto que sobrou (nome, @handle): vira a busca comum. */
  q?: string;
}

const norm = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const word = (s: string) => new RegExp(`(^|[^a-z0-9+#.])${escape(s)}(?=$|[^a-z0-9+#])`);

const SKILL_ALIASES: Record<string, string> = {
  js: "javascript",
  ts: "typescript",
  node: "nodejs",
  "node.js": "nodejs",
  nest: "nestjs",
  next: "nextjs",
  "next.js": "nextjs",
  three: "threejs",
  "three.js": "threejs",
  k8s: "kubernetes",
  postgresql: "postgres",
  pg: "postgres",
  mongo: "mongodb",
  rn: "react-native",
  "react native": "react-native",
  "c#": "csharp",
  ".net": "dotnet",
  "c++": "cpp",
  golang: "go",
  "machine learning": "ml",
  "aprendizado de maquina": "ml",
  "ia generativa": "ai",
  llm: "ai",
  seguranca: "security",
  tailwindcss: "tailwind",
  "spring boot": "spring",
  "ci/cd": "cicd",
  "system design": "system-design",
  ia: "ai",
  "em go": "go",
  "linguagem go": "go",
  "engenharia de dados": "data",
};

/** Slugs que também são palavras comuns em português ("aí", "data", "go"): só valem pelo rótulo. */
const AMBIGUOUS = new Set(["ai", "go", "c", "data"]);

const LANG_WORDS: ReadonlyArray<readonly [RegExp, string, string]> = [
  [/\b(fale|fala|falem|falando)?\s*(portugues|pt-?br)\b/, "pt", "fala português"],
  [/\b(ingles|english)\b/, "en", "fala inglês"],
  [/\b(espanhol|spanish|castelhano)\b/, "es", "fala espanhol"],
];

const COUNTRY_WORDS: ReadonlyArray<readonly [RegExp, string, string]> = [
  [/\b(do|no|de) brasil\b|\bbrasileir[oa]s?\b/, "BR", "no Brasil"],
  [/\b(de|em) portugal\b/, "PT", "em Portugal"],
  [/\b(da|na) argentina\b/, "AR", "na Argentina"],
  [/\b(do|no) mexico\b/, "MX", "no México"],
  [/\b(dos|nos) (eua|estados unidos)\b/, "US", "nos EUA"],
];

const STOP = new Set(
  "alguem que saiba saber sabe fale fala e esteja estejam de da do com um uma dev devs pessoa pessoas para programar quem conheca entenda manje manja em no na os as o a quero preciso procuro buscando busco agora hoje livre livres disponivel disponiveis online ajuda me".split(
    " ",
  ),
);

/**
 * "alguém que saiba WebGL, fale português e esteja livre agora" → { skills: ["webgl"], lang: "pt",
 * available: true }. Devolve também o que entendeu (para mostrar como chips) e, se nada for
 * reconhecido, o texto vira a busca comum.
 */
export function parseNaturalQuery(
  text: string,
  catalog: ReadonlyArray<{ slug: string; label: string }>,
): { filters: NaturalFilters; understood: string[] } {
  let t = ` ${norm(text)} `;
  const filters: NaturalFilters = { skills: [] };
  const understood: string[] = [];
  const take = (re: RegExp) => {
    t = t.replace(re, " ");
  };

  const labelOf = new Map(catalog.map((c) => [c.slug, c.label]));
  // Habilidades: apelidos primeiro (mais longos antes), depois rótulo e slug do catálogo.
  const candidates: Array<[string, string]> = [
    ...Object.entries(SKILL_ALIASES),
    ...catalog.flatMap((c): Array<[string, string]> => [
      [norm(c.label), c.slug],
      [c.slug, c.slug],
    ]),
  ]
    .filter(([term, slug]) => term.length >= 2 && !(term === slug && AMBIGUOUS.has(term)))
    .sort((a, b) => b[0].length - a[0].length);
  for (const [term, slug] of candidates) {
    if (!labelOf.has(slug)) continue;
    const re = word(term);
    if (re.test(t)) {
      if (!filters.skills.includes(slug)) {
        filters.skills.push(slug);
        understood.push(labelOf.get(slug)!);
      }
      take(new RegExp(re.source, "g"));
    }
  }

  for (const [re, code, label] of LANG_WORDS) {
    if (re.test(t)) {
      filters.lang ??= code;
      if (filters.lang === code) understood.push(label);
      take(new RegExp(re.source, "g"));
    }
  }
  for (const [re, code, label] of COUNTRY_WORDS) {
    if (!filters.country && re.test(t)) {
      filters.country = code;
      understood.push(label);
      take(re);
    }
  }
  if (/\b(livre|livres|disponivel|disponiveis|available|agora|now)\b/.test(t)) {
    filters.available = true;
    understood.push("livre agora");
  }
  if (/\bonline\b/.test(t)) {
    filters.online = true;
    understood.push("online");
  }
  const goals: ReadonlyArray<readonly [RegExp, NaturalFilters["goal"], string]> = [
    [/\b(quer(em)? aprender|aprendendo|aprender)\b/, "aprender", "quer aprender"],
    [/\b(quer(em)? ensinar|ensinar|mentor(a|ia)?)\b/, "ensinar", "quer ensinar"],
    [/\bhackathon\b/, "hackathon", "hackathon"],
    [/\b(construir|criar um projeto)\b/, "construir", "quer construir"],
  ];
  for (const [re, goal, label] of goals) {
    if (!filters.goal && re.test(t)) {
      filters.goal = goal;
      understood.push(label);
      take(re);
    }
  }
  const levels: ReadonlyArray<readonly [RegExp, NaturalFilters["level"], string]> = [
    [/\biniciantes?\b/, "iniciante", "iniciante"],
    [/\bjuniors?\b/, "junior", "júnior"],
    [/\bplenos?\b/, "pleno", "pleno"],
    [/\bseniors?\b/, "senior", "sênior"],
  ];
  for (const [re, level, label] of levels) {
    if (!filters.level && re.test(t)) {
      filters.level = level;
      understood.push(label);
      take(re);
    }
  }

  // Nada reconhecido: o texto inteiro é a busca comum (nome, @handle, interesse).
  if (!understood.length) {
    const q = text.trim();
    if (q) filters.q = q.slice(0, 100);
  } else {
    const rest = t
      .replace(/[,.;!?]/g, " ")
      .split(/\s+/)
      .filter((w) => w.length > 2 && !STOP.has(w));
    const handle = rest.find((w) => w.startsWith("@"));
    if (handle) filters.q = handle;
  }
  return { filters, understood };
}

// ───────────── Montador de time (hackathon) ─────────────

export const TEAM_SLOTS = [
  { id: "front", label: "front" },
  { id: "back", label: "back" },
  { id: "design", label: "design" },
  { id: "dados", label: "dados" },
  { id: "infra", label: "infra" },
] as const satisfies ReadonlyArray<{ id: StackGroupId; label: string }>;

export interface TeamMember {
  skills: ReadonlyArray<{ slug: string; proficiency: number }>;
  hours: readonly boolean[];
}

/** O que o time cobre (front ✓ back ✓ design ✗…) e quantas horas por dia todos estão juntos. */
export function teamCoverage(members: readonly TeamMember[]) {
  const covered = Object.fromEntries(
    TEAM_SLOTS.map((slot) => [
      slot.id,
      members.some((m) =>
        m.skills.some((s) => s.proficiency >= 2 && (GROUP_OF.get(s.slug) ?? []).includes(slot.id)),
      ),
    ]),
  ) as Record<(typeof TEAM_SLOTS)[number]["id"], boolean>;
  return {
    covered,
    missing: TEAM_SLOTS.filter((s) => !covered[s.id]).map((s) => s.id),
    commonHours: members.length > 1 ? commonHours(...members.map((m) => m.hours)) : 24,
  };
}

/** Quem cobre a maior parte do que falta (para sugerir o próximo do time). */
export function fillsGap(member: TeamMember, missing: readonly StackGroupId[]): number {
  let n = 0;
  for (const slot of missing)
    if (member.skills.some((s) => s.proficiency >= 2 && (GROUP_OF.get(s.slug) ?? []).includes(slot))) n++;
  return n;
}

// ───────────── Entradas da API ─────────────

/** Pedido de ajuda no mural (opt-in): o que travou, a stack e, se quiser, o projeto. */
export const HelpRequestInput = z.object({
  message: z.string().trim().min(5, "Conte em poucas palavras onde travou.").max(280),
  skills: z.array(z.string().trim().toLowerCase().min(1).max(40)).max(5).default([]),
  projectId: z.uuid().optional(),
  /** Quem clicar em "Entrar como mentor" entra no projeto com o papel de mentor (só sugere). */
  allowMentors: z.boolean().default(true),
});
export type HelpRequestInput = z.infer<typeof HelpRequestInput>;

/** "Chamar para sessão": convite para programar junto agora, com papel e tempo definidos. */
export const SESSION_MINUTES = [15, 25, 45, 60] as const;
export const SessionCallInput = z.object({
  userId: z.uuid(),
  projectId: z.uuid(),
  role: z.enum(["driver", "navigator"]).default("navigator"),
  minutes: z.coerce
    .number()
    .int()
    .refine((m) => (SESSION_MINUTES as readonly number[]).includes(m), "Tempo inválido.")
    .default(25),
});
export type SessionCallInput = z.infer<typeof SessionCallInput>;

/** Quanto tempo um pedido de ajuda fica no mural sem ser renovado. */
export const HELP_REQUEST_TTL_MS = 3 * 60 * 60_000;
